import { useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import keyBy from 'lodash/fp/keyBy';

import { GET_TALENTS_FOR_TALENT_BOARD } from '@bloomays-lib/adapter.api-talent';
import { ApolloGqlTalentForTalentBoard } from '@bloomays-lib/types.shared';
import { Logger } from '../services/service-logger';
import useTalentsForTalentBoardStore from '../stores/talentsForTalentBoardStore';
import { monitorException } from '@bloomays-lib/common.monitoring/browser';
const logger = Logger('useTalentsForTalentBoard');

type GetTalentsForTalentBoardResponse = {
  getTalentsForTalentBoard: ApolloGqlTalentForTalentBoard[];
};

export const useTalentsForTalentBoard = (jobId: string) => {
  const { getTalents, setTalents, setLoading, setError, mustRefreshTalentsForTalentBoard, reset } =
    useTalentsForTalentBoardStore();

  // Récupère les talents pour le jobId spécifique
  const talents = getTalents(jobId);

  // useLazyQuery ne se déclenche pas automatiquement
  const [fetchTalentsForTalentBoard, { data, loading, error, refetch }] =
    useLazyQuery<GetTalentsForTalentBoardResponse>(GET_TALENTS_FOR_TALENT_BOARD, {
      context: { clientName: 'api.talents' },
      fetchPolicy: 'cache-and-network', // on force le fetch pour éviter de récupérer les données en cache, si le mustRefreshTalentsForTalentBoard est true
      variables: { jobId },
    });

  // Hydratation conditionnelle : fetch seulement si data in store n'est plus valide pour ce jobId
  useEffect(() => {
    const shouldFetch = mustRefreshTalentsForTalentBoard(jobId);

    if (shouldFetch) {
      void fetchTalentsForTalentBoard();
    }
  }, [jobId, talents, fetchTalentsForTalentBoard, mustRefreshTalentsForTalentBoard]);

  // Synchro loading
  useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);

  // Synchro error
  useEffect(() => {
    if (error) {
      logger.error('useTalentsForTalentBoard > error graphql', {}, error);
      monitorException(error, { name: 'useTalentsForTalentBoard', jobId });
      setError(error as Error);
    }
  }, [error, jobId, setError]);

  // Synchro data
  useEffect(() => {
    if (data?.getTalentsForTalentBoard) {
      const talents = keyBy('id', data.getTalentsForTalentBoard);
      try {
        setTalents(jobId, talents);
      } catch (error) {
        logger.error('useTalentsForTalentBoard > error setTalents', {}, error as Error);
        monitorException(error, { name: 'useTalentsForTalentBoard', jobId });
        if (error instanceof DOMException && error.name === 'QuotaExceededError') {
          reset();
          setTalents(jobId, talents);
        }
      }
    }
  }, [data, jobId, setTalents]);

  // Fallback: si erreur réseau mais des données sont persistées en store, on les expose
  const effectiveLoading = loading && talents === null;

  return {
    talents,
    loading: effectiveLoading,
    error,
    refetch,
  };
};

export default useTalentsForTalentBoard;
